<template>
  <Addition v-if="isAddition">
    <template #field>{{ change.field.name }}</template>
    <v-data-table
      :headers="headers"
      :items="openingHours"
      hide-default-header
      hide-default-footer
      class="comparison-table"
    >
      <template #header.afterVal="{ header }">
        <small>{{ header.text }}</small>
      </template>
      <template #item.afterVal="{ item }">
        <p
          v-for="(oldHour, index) in item.afterVal"
          :key="`addition-after-${index}`"
        >
          {{ oldHour.join(" - ") }}
        </p>
      </template>
    </v-data-table>
  </Addition>
  <Removal v-else-if="isRemoval">
    <template #field>{{ change.field.name }}</template>
    <v-data-table
      :headers="headers"
      :items="openingHours"
      hide-default-header
      hide-default-footer
      class="comparison-table comparison-table--removal"
    >
      <template #header.beforeVal="{ header }">
        <small>{{ header.text }}</small>
      </template>
      <template #item.beforeVal="{ item }">
        <p
          v-for="(oldHour, index) in item.beforeVal"
          :key="`removal-before-${index}`"
        >
          {{ oldHour.join(" - ") }}
        </p>
      </template>
    </v-data-table>
  </Removal>
  <Change v-else-if="isChange" class="opening-hours">
    <template #field>{{ change.field.name }}</template>
    <v-data-table
      :headers="headers"
      :items="openingHours"
      hide-default-footer
      class="comparison-table"
    >
      <template #header.beforeVal="{ header }">
        <small>{{ header.text }}</small>
      </template>
      <template #header.afterVal="{ header }">
        <small>{{ header.text }}</small>
      </template>
      <template #item.beforeVal="{ item }">
        <p
          v-for="(oldHour, index) in item.beforeVal"
          :key="`change-before-${index}`"
        >
          {{ oldHour.join(" - ") }}
        </p>
      </template>
      <template #item.afterVal="{ item }">
        <p
          v-for="(newHour, index) in item.afterVal"
          :key="`change-after-${index}`"
          :class="{ 'new-value': item.changed }"
        >
          {{ newHour.join(" - ") }}
        </p>
      </template>
    </v-data-table>
  </Change>
</template>

<script>
import { EXTERNAL_CHANGES_TYPE } from "@/enums/external-changes";
import { WEEK_DAYS } from "@/enums/week-days";

import Addition from "@/components/ExternalChanges/general/Addition.vue";
import Removal from "@/components/ExternalChanges/general/Removal.vue";
import Change from "@/components/ExternalChanges/general/Change.vue";

export default {
  name: "OpeningHoursChange",
  data() {
    return {};
  },
  props: {
    change: {
      type: Object,
      required: true
    }
  },
  components: { Addition, Removal, Change },

  computed: {
    isAddition() {
      return this.change && this.change.type
        ? this.change.type === EXTERNAL_CHANGES_TYPE.ADDITION &&
            this.change.new_value
        : false;
    },
    isRemoval() {
      return this.change && this.change.type
        ? this.change.type === EXTERNAL_CHANGES_TYPE.REMOVAL &&
            this.change.old_value
        : false;
    },
    isChange() {
      return this.change && this.change.type
        ? this.change.type === EXTERNAL_CHANGES_TYPE.CHANGE &&
            this.change.new_value &&
            this.change.old_value
        : false;
    },
    headers() {
      const commonProps = {
        class: "header-cell",
        sortable: false
      };

      const headers = [
        {
          text: "",
          value: "label",
          ...commonProps
        }
      ];

      if (this.isRemoval || this.isChange) {
        headers.push({
          text: this.$t("message.common.before"),
          value: "beforeVal",
          ...commonProps
        });
      }
      if (this.isAddition || this.isChange) {
        headers.push({
          text: this.$t("message.common.after"),
          value: "afterVal",
          ...commonProps
        });
      }

      return headers;
    },
    openingHours() {
      const openingHoursData = [];

      Object.keys(WEEK_DAYS).forEach(key => {
        const openingHoursItem = {};
        const newValue = this.change.new_value[key];
        const oldValue = this.change.old_value[key];

        if (
          (newValue &&
            oldValue &&
            JSON.stringify(newValue) !== JSON.stringify(oldValue)) ||
          (!newValue && oldValue) ||
          (newValue && !oldValue)
        ) {
          openingHoursItem["changed"] = true;
        } else {
          openingHoursItem["changed"] = false;
        }
        openingHoursItem["label"] = this.$t(
          `message.weekDaysShort.${WEEK_DAYS[key]}`
        );
        openingHoursItem["id"] = WEEK_DAYS[key];
        openingHoursItem["beforeVal"] = oldValue || [
          [this.$t("message.common.closed")]
        ];
        openingHoursItem["afterVal"] = newValue || [
          [this.$t("message.common.closed")]
        ];

        openingHoursData.push(openingHoursItem);
      });

      return openingHoursData.sort((a, b) => a.id - b.id);
    }
  }
};
</script>

<style lang="scss" scoped>
p {
  margin-bottom: 0;
}
small {
  @include font-size(12px);
  opacity: 0.6;
}
.new-value {
  font-weight: bold;
}
</style>

<style lang="scss">
.comparison-table {
  background-color: transparent !important;
  position: relative;

  &--removal {
    text-decoration: line-through;
  }

  &.theme--light.v-data-table {
    tbody tr td,
    thead tr th {
      border-bottom: none !important;
      height: unset;
      padding-top: $spacing * 0.25;
      padding-bottom: $spacing * 0.25;
      vertical-align: top;

      &:nth-child(3) {
        padding-left: $spacing * 3 !important;
      }
    }

    thead tr th {
      font-weight: 400;
    }

    tbody tr td:first-child {
      padding-left: 0;
    }

    tbody tr:not(:nth-child(4)) td:nth-child(3) {
      position: relative;

      &:before {
        position: absolute;
        content: " ";
        bottom: 0px;
      }

      &:before {
        position: absolute;
        left: 0;
        height: 100%;
        width: 1px;
        background-color: $gray-300;
      }
    }

    tbody tr:nth-child(4) td:nth-child(3) {
      position: relative;

      &:after,
      &:before {
        position: absolute;
        content: " ";
        bottom: 0px;
      }

      &:before {
        position: absolute;
        left: 0;
        height: 100%;
        width: 1px;
        background-color: $gray-300;
      }

      &:after {
        display: inline-block;
        position: absolute;
        top: calc(50% - 10px);
        right: calc(100% - 2px);
        height: 10px;
        width: 10px;
        transform: rotate(135deg) translateY(-50%);
        border-top: 1px solid $gray-300;
        border-left: 1px solid $gray-300;
        background-color: $light-4;
      }
    }

    tbody tr:hover {
      background: transparent !important;
    }
  }
}
</style>
